<template>
  <v-dialog v-model="dialog" max-width="720px" persistent scrollable :content-class="dialogContentClass">
    <v-card class="asset-dialog-card">
      <v-form ref="form" class="asset-dialog-form">
        <v-card-title class="asset-dialog__title">
          <span class="asset-dialog__title-icon">
            <v-icon color="white">mdi-database-edit-outline</v-icon>
          </span>
          <span>
            <span class="asset-dialog__title-text d-block">Edit Asset</span>
            <span class="asset-dialog__subtitle d-block">Update reusable data for your automations</span>
          </span>
        </v-card-title>
        <v-card-text class="asset-dialog__body">
          <v-alert
            v-show="!!error"
            text
            prominent
            type="error"
            icon="mdi-alert-circle"
            >{{ error }}</v-alert
          >
          <v-alert
            v-show="!!success"
            text
            prominent
            type="success"
            icon="mdi-cloud-check"
            >Successfully Updated</v-alert
          >
          <v-container class="text-center" v-if="loading">
            <v-progress-circular
              :size="70"
              :width="7"
              color="purple"
              indeterminate
            ></v-progress-circular>
          </v-container>
          <v-container v-else fluid>
            <section class="asset-section">
              <div class="asset-section__head">
                <span class="asset-section__icon"><v-icon small color="white">mdi-information-outline</v-icon></span>
                <span>
                  <span class="asset-section__title d-block">Asset details</span>
                  <span class="asset-section__hint d-block">Keep the asset identity clear and reusable</span>
                </span>
              </div>
              <v-row dense>
                <v-col cols="12" md="6">
                <v-text-field
                  v-model="item.Name"
                  label="Name"
                  placeholder="e.g. Muavin_1001"
                  outlined
                  dense
                  required
                  :rules="nameRules"
                  maxlength="50"
                  counter
                  :disabled="success"
                  @keypress="nameControl($event)"
                ></v-text-field>
              </v-col>
                <v-col cols="12" md="6">
                <v-text-field
                  v-model="item.Description"
                  label="Description"
                  outlined
                  dense
                  maxlength="200"
                  counter
                  :disabled="success"
                ></v-text-field>
              </v-col>
              </v-row>
            </section>

            <section class="asset-section">
              <div class="asset-section__head">
                <span class="asset-section__icon"><v-icon small color="white">mdi-tag-multiple-outline</v-icon></span>
                <span>
                  <span class="asset-section__title d-block">Labels</span>
                  <span class="asset-section__hint d-block">Organize and find this asset more easily</span>
                </span>
              </div>
              <div class="asset-label-row">
                <v-autocomplete
                  v-model="labelIds"
                  :items="labels"
                  item-text="Name"
                  item-value="Id"
                  outlined
                  dense
                  multiple
                  label="Select Labels"
                  hide-details
                  :disabled="success"
                ></v-autocomplete>
                <v-btn
                  v-if="hasAuth('Label Action')"
                  class="asset-label-button"
                  color="primary"
                  outlined
                  v-on:click="openLabelDialog"
                  :disabled="success"
                  ><v-icon small left>mdi-tag-plus-outline</v-icon>Manage</v-btn
                >
              </div>
            </section>

            <section class="asset-section">
              <div class="asset-section__head">
                <span class="asset-section__icon"><v-icon small color="white">mdi-shape-outline</v-icon></span>
                <span>
                  <span class="asset-section__title d-block">Type and value</span>
                  <span class="asset-section__hint d-block">Choose how this asset stores its data</span>
                </span>
              </div>
              <v-row dense>
                <v-col cols="12">
                <v-autocomplete
                  v-model="item.AssetType"
                  :items="types"
                  item-text="Name"
                  item-value="Id"
                  outlined
                  dense
                  label="Select Type"
                  :rules="typeRules"
                  :disabled="success"
                  v-on:change="changeType"
                  return-object
                ></v-autocomplete>
              </v-col>
                <v-col v-if="textType" cols="12">
                <v-text-field
                  v-model="item.Value"
                  label="Value"
                  outlined
                  dense
                  maxlength="200"
                  counter
                  :disabled="success"
                ></v-text-field>
              </v-col>
                <v-col v-if="credentialType" cols="12" sm="6">
                <v-text-field
                  v-model="item.Username"
                  label="Username"
                  outlined
                  dense
                  maxlength="100"
                  counter
                  :disabled="success"
                ></v-text-field>
              </v-col>
                <v-col v-if="credentialType" cols="12" sm="6">
                <v-text-field
                  v-model="item.Password"
                  label="Password"
                  outlined
                  dense
                  maxlength="500"
                  counter
                  type="password"
                  :disabled="success"
                ></v-text-field>
              </v-col>
            </v-row>
            </section>
          </v-container>
        </v-card-text>
        <v-card-actions class="asset-dialog__actions">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="toggleDialog">Cancel</v-btn>
          <v-btn
            depressed
            color="primary"
            v-show="!success && !loading"
            @click="save"
            ><v-icon small left>mdi-content-save-outline</v-icon>Save</v-btn
          >
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import {
  FETCH_LABELS,
  FETCH_TYPES,
  UPDATE_ASSET,
  TOGGLE_UPDATE_ASSET_DIALOG,
  TOGGLE_LABEL_DIALOG,
  CHANGE_DIALOG,
} from "../../store/modules/asset";
import { hasActionAuthorization } from "../../authorization";

export default {
  data: () => ({
    textType: false,
    credentialType: false,
    nameRules: [
      (v) => !!v || "Asset name is required",
      (v) =>
        /^[A-Za-z_][A-Za-z0-9_]*$/.test(v) ||
        "Name must start with a letter or underscore; only letters, numbers and underscore allowed (e.g. Muavin_1001)",
    ],
    typeRules: [(v) => !!v || "Type is required"],
    labelIds: [],
    assetTypeId: "",
  }),
  computed: {
    ...mapState("asset", {
      item: (state) => state.updateAsset.item,
      labels: (state) => state.labels,
      types: (state) => state.types,
      loading: (state) => state.updateAsset.isLoading,
      success: (state) => state.updateAsset.success,
      error: (state) => state.updateAsset.error,
      dialog: (state) => state.updateAsset.dialog,
      labelSheetOpen: (state) => state.labelDialog,
    }),
    dialogContentClass() {
      return this.labelSheetOpen ? "asset-dialog asset-dialog--shifted" : "asset-dialog";
    },
  },
  watch: {
    dialog(value) {
      if (value) this.changeType();
    },
    item(value) {
      if (value) this.labelIds = value.Labels;
      else this.labelIds = [];
    },
  },
  mounted() {
    this.fetchTypes();
    this.fetchLabels();
  },
  methods: {
    ...mapActions("asset", {
      fetchLabels: FETCH_LABELS,
      fetchTypes: FETCH_TYPES,
      updateAsset: UPDATE_ASSET,
      toggleDialog: TOGGLE_UPDATE_ASSET_DIALOG,
      toggleLabelDialog: TOGGLE_LABEL_DIALOG,
      changeDialog: CHANGE_DIALOG,
    }),
    save() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.updateAsset({
        assetId: this.item.Id,
        asset: {
          Name: this.item.Name,
          Description: this.item.Description,
          Value: this.item.Value,
          Username: this.item.Username,
          Password: this.item.Password,
          Labels: this.labelIds,
          AssetTypeId: this.item.AssetType.Id,
        },
      });
    },
    changeType() {
      if (!this.item.AssetType) {
        this.clearTextType();
        this.clearCredentialType();
      }

      if (this.item.AssetType.Name === "Text") {
        this.clearCredentialType();
        this.textType = true;
      } else if (this.item.AssetType.Name === "Credential") {
        this.clearTextType();
        this.credentialType = true;
      } else {
        this.clearTextType();
        this.clearCredentialType();
      }
    },
    clearTextType() {
      this.textType = false;
      this.item.Value = "";
    },
    clearCredentialType() {
      this.credentialType = false;
      this.item.Username = "";
      this.item.Password = "";
    },
    hasAuth(action) {
      return hasActionAuthorization(this.$store, "Assets", action);
    },
    openLabelDialog() {
      this.toggleLabelDialog();
    },
    nameControl(event) {
      if (!event.key || event.key.length !== 1) return;
      const current = this.item.Name || "";
      const allowed = current.length === 0 ? /[A-Za-z_]/ : /[A-Za-z0-9_]/;
      if (!allowed.test(event.key)) {
        event.preventDefault();
      }
    },
  },
};
</script>
